import { getRefreshToken, getSessionToken, refresh } from '@descope/react-sdk';
import { rewriteSessionSlots } from '@/features/viewAs/sessionSlots';

// Descope session helpers. The React SDK owns storage/refresh — we just expose
// a plain accessor for non-React code (services/api.ts, services/vhSearch.ts)
// to read the current session JWT for `Authorization: Bearer`.

export interface UserProfile {
  email: string;
  name: string;
  picture?: string;
  sub: string;
}

export const isConfigured = Boolean(import.meta.env.VITE_DESCOPE_PROJECT_ID);

export function getIdToken(): string {
  return getSessionToken() ?? '';
}

/**
 * Renew the session JWT and return the fresh one, or '' when the renewal
 * itself failed (session truly over — the caller keeps its original error).
 * Exists for the stale-token window: the SDK refreshes on its own timer, and a
 * request sent in the gap gets a 401 that a refresh-and-retry heals.
 */
export async function refreshIdToken(): Promise<string> {
  // A session swap has already written the next identity's token and is waiting
  // for the page to go. Refreshing now would mint from — and re-persist — the
  // identity we are LEAVING, straight over the one just written, and the reload
  // would come back as the wrong person with nothing on screen to say so.
  if (swapping) return '';
  try {
    const res = await refresh();
    if (!res.ok) return '';
  } catch {
    return '';
  }
  return getIdToken();
}

/** True from the moment a swap has written the new token until the page goes.
 *  `location.replace` does not stop script execution, so this is the window in
 *  which our own code must not touch the session. */
let swapping = false;

/** The refresh token this browser is holding. View-as trades it back for the
 *  admin's own on the way out, so the server needs to be handed the exact one. */
export function getRefreshJwt(): string {
  return getRefreshToken() ?? '';
}

/** A single global key holding the Brand-impersonation screen's open tabs. Not
 *  scoped to a user, so tabs opened while viewing a customer would otherwise
 *  overwrite the admin's own layout and outlive the session. */
const SCREEN_TABS_KEY = 'vh:impersonation:tabs';

/**
 * Become whoever `refreshJwt` belongs to, and reload. Throws if the swap could
 * not be made — never navigate on a swap that did not land.
 *
 * Both directions of view-as end here: the customer's token on the way in, the
 * admin's own on the way out. The reload is not a shortcut — every screen, every
 * cache and every open request belongs to the previous identity, and a full load
 * is the only honest way to leave none of it behind.
 *
 * What a reload does NOT clear is storage, and the tab keeps that across the
 * navigation: the pending-turn note would steer the new identity into the
 * previous one's chat, and the screen's tab layout would follow them across.
 * Both are per-identity by nature, so both go.
 *
 * `replace`, and to "/": the current URL may be a chat belonging to the identity
 * we are leaving, so keeping it in history just puts a Back button on an error
 * screen.
 */
export function adoptSession(refreshJwt: string): void {
  swapping = true;
  if (!rewriteSessionSlots(window.localStorage, getRefreshJwt(), getIdToken(), refreshJwt)) {
    // The slot could not be identified by the token it held, so the write may
    // have gone somewhere the SDK never reads. Navigating here is what produced
    // a silent return to the previous identity; say so instead.
    swapping = false;
    throw new Error(
      'Could not switch sessions in this browser. Check that site data is not blocked, then try again.',
    );
  }
  try {
    // Per-tab scratch: by definition it belongs to the identity being left.
    window.sessionStorage.clear();
    window.localStorage.removeItem(SCREEN_TABS_KEY);
  } catch {
    /* storage blocked — the session swap above is what matters */
  }
  window.location.replace('/');
}
